import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { CaretDownIconSmall } from "../Icons/Icons";

interface DropdownItem {
  id: number;
  label: string;
  href?: string;
  target?: string;
  onClick?: () => void;
}

interface NavDropdownProps {
  label: string;
  items: DropdownItem[];
  className?: string;
}

const NavDropdown = ({ label, items, className = "" }: NavDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [transitionStatus, setTransitionStatus] = useState<"open" | "close">(
    "close",
  );
  const [position, setPosition] = useState<{
    alignRight: boolean;
    openUpward: boolean;
  }>({
    alignRight: false,
    openUpward: false,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    if (isOpen) {
      setTransitionStatus("close");
      setTimeout(() => {
        setIsOpen(false);
      }, 100);
    } else {
      setIsOpen(true);
      setTransitionStatus("open");
    }
  };

  const handleItemClick = (item: DropdownItem) => {
    if (item.onClick) {
      item.onClick();
    }
    if (!item.href) {
      setTransitionStatus("close");
      setTimeout(() => {
        setIsOpen(false);
      }, 100);
    }
  };

  const closeDropdown = () => {
    setTransitionStatus("close");
    setTimeout(() => {
      setIsOpen(false);
    }, 100);
  };

  useEffect(() => {
    if (isOpen && dropdownRef.current && containerRef.current) {
      const dropdown = dropdownRef.current;
      const container = containerRef.current;
      const dropdownRect = dropdown.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const overflowsRight = dropdownRect.right > viewportWidth;
      const hasSpaceOnLeft = containerRect.left >= dropdownRect.width;

      const overflowsBottom = dropdownRect.bottom > viewportHeight;
      const hasSpaceAbove = containerRect.top >= dropdownRect.height;

      setPosition({
        alignRight: overflowsRight && hasSpaceOnLeft,
        openUpward: overflowsBottom && hasSpaceAbove,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setTransitionStatus("close");
        setTimeout(() => {
          setIsOpen(false);
        }, 100);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isOpen && event.key === "Escape") {
        setTransitionStatus("close");
        setTimeout(() => {
          setIsOpen(false);
        }, 100);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        onClick={toggleDropdown}
        className="flex min-h-[44px] py-2 px-4 items-center justify-center gap-2 cursor-pointer rounded-lg hover:bg-gray-200 transition-colors"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="text-sm">{label}</span>
        <CaretDownIconSmall
          className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className={`popover-scale absolute min-w-[200px] bg-white rounded-lg border border-[#CCCCD1] shadow-[0_4px_12px_0_rgba(0,0,0,0.10)] z-50 ${
            position.openUpward ? "bottom-full mb-2" : "top-full mt-2"
          } ${position.alignRight ? "right-0" : "left-0"}`}
          data-transition-status={transitionStatus}
        >
          <ul className="list-none m-0 p-2">
            {items.map((item) => (
              <li
                key={item.id}
                className="flex items-center rounded-lg hover:bg-gray-200 transition-colors"
                role="menuitem"
              >
                {item.href ? (
                  <Link
                    href={item.href}
                    target={item.target}
                    rel={
                      item.target === "_blank"
                        ? "noopener noreferrer"
                        : undefined
                    }
                    onClick={closeDropdown}
                    className="flex items-center px-4 py-3 w-full cursor-pointer"
                  >
                    <span className="text-sm">{item.label}</span>
                  </Link>
                ) : (
                  <div
                    onClick={() => handleItemClick(item)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleItemClick(item);
                      }
                    }}
                    className="flex items-center px-4 py-3 w-full cursor-pointer"
                    tabIndex={0}
                  >
                    <span className="text-sm">{item.label}</span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default NavDropdown;
